import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { AlertShort, Loader, TextLink } from '@sdir/sds';
import moment from 'moment';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { CourseEducationResult, ProcessingState } from '@sdir/httpclient/lib/clients/aps/course';
import {
  CoursAndEducationsHeader,
  CoursAndEducationRow,
  TableDefinition,
  CoursAndEducationsHeaderMobile,
  CoursAndEducationRowMobile,
} from '../../../helpers/TableHeaders';
import { SortableDataTable } from '../../Molecules';
import { courseEducationApi } from '../../../httpclient';

export const MyCourses: React.FC = () => {
  const intl = useIntl();

  const {
    result: educations,
    errorObject,
    loading,
    callApi,
  } = useApi({
    apiFunction: courseEducationApi.v1CourseeducationUsersowncoursesQueryObjectPost,
    callApiOnInit: true,
    bind: courseEducationApi,
    defaultPayload: {
      filter: ProcessingState.Approved,
      includeExpired: true,
    },
  });
  const [showExpired, setShowExpired] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const toggleExpired = useCallback(() => {
    setShowExpired(!showExpired);
  }, [callApi, showExpired]);

  const filteredEducations = useMemo(() => {
    if (showExpired) return educations;
    return educations?.filter((i) => i.valid);
  }, [showExpired, educations]);

  const table = useMemo<TableDefinition<CoursAndEducationRow> | null>(() => {
    if (!filteredEducations) return null;
    return {
      columns: CoursAndEducationsHeader(intl.formatMessage),
      data: filteredEducations?.map((e) => {
        const expiryFormatted = moment(e.expiryDate).format('DD.MM.YYYY') || '';
        return {
          courseoreducation: e.valid ? (
            e.courseName || ''
          ) : (
            <div className="expired">
              <div className="expired-state" />
              <span>{e.courseName || ''}</span>
            </div>
          ),
          issued: moment(e.issuedDate).format('DD.MM.YYYY') || '',
          issuedValue: moment(e.issuedDate),
          expiry: e.valid
            ? expiryFormatted
            : `${intl.formatMessage({ id: 'general.table.expired' })} ${expiryFormatted}`,
          expiryValue: moment(e.expiryDate),
        };
      }),
      sortableKeys: '*',
    };
  }, [filteredEducations, intl]);

  const mobileTable = useMemo<TableDefinition<CoursAndEducationRowMobile> | null>(() => {
    if (!filteredEducations) return null;
    return {
      columns: CoursAndEducationsHeaderMobile(intl.formatMessage),
      data: filteredEducations?.map((e) => ({
        courseoreducation: e.courseName || '',
        childContent: <RowMobileContent rowData={e} fm={intl.formatMessage} />,
      })),
      sortableKeys: ['courseoreducation'],
      collapsable: true,
      collapsableText: [
        intl.formatMessage({ id: 'general.table.viewdetails' }),
        intl.formatMessage({ id: 'general.table.hidedetails' }),
      ],
    };
  }, [filteredEducations, intl]);

  const showEmpty = !educations || educations.length === 0;

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Wrapper>
      <h2>{intl.formatMessage({ id: 'home.tile.courseAndEducation' })}</h2>
      {loading && <Loader />}
      {errorObject && <ErrorMessage error={errorObject} />}
      {showEmpty && (
        <AlertShort text={intl.formatMessage({ id: 'courseandeducation.notfound' })} type="info" />
      )}
      {table && mobileTable && !showEmpty && (
        <>
          <TableWrapper data-testid="course-table">
            <StyledSortableDataTable table={isMobile ? mobileTable : table} />
          </TableWrapper>
          <TextLink
            text={
              showExpired
                ? intl.formatMessage({ id: 'courseandeducation.table.hideexpired' })
                : intl.formatMessage({ id: 'courseandeducation.table.showexpired' })
            }
            onClick={toggleExpired}
          />
        </>
      )}
    </Wrapper>
  );
};

interface RowMobileContentProps {
  rowData: CourseEducationResult;
  fm: any;
}

const RowMobileContent: React.FC<RowMobileContentProps> = ({ rowData, fm }) => {
  return (
    <>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'courseandeducation.table.subheader2' })}</SubHeader>
        <SubValue>{rowData.organization || ''}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'courseandeducation.table.subheader3' })}</SubHeader>
        <SubValue>{fm({ id: rowData.valid ? 'valid' : 'invalid' })}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'courseandeducation.table.subheader4' })}</SubHeader>
        <SubValue>{moment(rowData.issuedDate).format('DD.MM.YYYY') || ''}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'courseandeducation.table.subheader5' })}</SubHeader>
        <SubValue>{moment(rowData.expiryDate).format('DD.MM.YYYY') || ''}</SubValue>
      </MobileContentItem>
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const TableWrapper = styled.div`
  max-width: 100%;
  overflow: auto;

  table tr:has(.expired) {
    background-color: #d9d9d940;
  }

  & div.expired {
    position: relative;
    display: flex;
    &-state {
      position: absolute;
      height: 50px;
      width: 6px;
      top: -17px;
      left: -3px;
      background-color: ${({ theme }) => theme.colors.alert.errorDark};
    }
    span {
      margin-left: 10px;
    }
  }
`;

const StyledSortableDataTable = styled(SortableDataTable)`
  margin-left: -5px;
  padding: 0 0 20px 0;
  background-color: transparent;
  thead tr th {
    padding-left: 0;
    button {
      text-align: left;
    }
  }
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }

    & td:last-child {
      white-space: nowrap;
    }
  }
`;

const MobileContentItem = styled.div`
  margin-bottom: 10px;
`;

const SubHeader = styled.span`
  display: block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const SubValue = styled.span`
  display: block;
`;
